import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import SEO from "../components/seo/SEO";
import CryptoJS from "crypto-js";
import ApiServices from "../../Utils/ApiServices/ApiServices";

export default function ProviderLogin() {
  const navigate = useNavigate();
  const secretKey = process.env.REACT_APP_USER_ENC_DEC_KEY;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState("");

  /* ================= LOGIN ================= */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = {
        user_role: "users",
        username,
        password,
        client_id: process.env.REACT_APP_CLINTID_ACS_TKN,
        scope: process.env.REACT_APP_PROVIDER_SCOPES,
        grant_type: "password",
      };

      const { data, error } = await ApiServices.post(
        "/oauth2/default/token",
        payload,
        { "Content-Type": "application/x-www-form-urlencoded" }
      );

      if (error) {
        throw new Error(
          error.response?.data?.error_description || "Login failed"
        );
      }

      const practitionerRes = await ApiServices.get(
        `/apis/default/api/practitioner/${username}`,
        {
          Authorization: `Bearer ${data.access_token}`,
        }
      );

      const provider = practitionerRes.data["0"];

      const capitalizedName =
        provider.fname.charAt(0).toUpperCase() +
        provider.fname.slice(1) +
        " " +
        provider.lname.charAt(0).toUpperCase() +
        provider.lname.slice(1);

      setWelcomeMessage(`Welcome back to work, ${capitalizedName}`);
      setWelcomeOpen(true);

      const encryptedUserData = CryptoJS.AES.encrypt(
        JSON.stringify(provider),
        secretKey
      ).toString();

      const encryptedUserType = CryptoJS.AES.encrypt(
        provider.user_type,
        secretKey
      ).toString();

      const encryptedAccessToken = CryptoJS.AES.encrypt(
        data.access_token,
        secretKey
      ).toString();

      const encryptedRefreshToken = CryptoJS.AES.encrypt(
        data.refresh_token,
        secretKey
      ).toString();

      sessionStorage.setItem("LoginUserDetails", encryptedUserData);
      sessionStorage.setItem("LoginUserType", encryptedUserType);
      sessionStorage.setItem("LoginUserAccessToken", encryptedAccessToken);
      sessionStorage.setItem("LoginUserRefreshToken", encryptedRefreshToken);

      setTimeout(() => {
        setWelcomeOpen(false);
        navigate("/provider/dashboard");
      }, 2500);
    } catch (err) {
      setError("Failed to login. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Provider Login"
        description="Secure provider login for Rite Wellness"
        canonical="/provider-emr-login"
        noIndex
      />

      <Box
        component="main"
        className="wellness-bg"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 12,
          px: 2,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ width: "100%", maxWidth: 420 }}
        >
          <Box className="glass-card" sx={{ p: 4 }}>
            {/* ===== BRAND ===== */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                component={Link}
                to="/"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  textDecoration: "none",
                  mb: 3,
                  color: "text.primary",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "12px",
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FavoriteRoundedIcon sx={{ color: "#fff", fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
                  Rite
                  <Box component="span" sx={{ color: "primary.main" }}>
                    Wellness
                  </Box>
                </Typography>
              </Box>

              <Typography sx={{ fontSize: 24, fontWeight: 800 }}>
                Provider Login
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 14, mt: 1 }}>
                Doctors & staff secure access
              </Typography>
            </Box>

            {/* ===== FORM ===== */}
            <Box
              component="form"
              onSubmit={handleLogin}
              sx={{ display: "grid", gap: 2 }}
            >
              <TextField
                placeholder="Username or Email"
                fullWidth
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />

              <TextField
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOffRoundedIcon />
                        ) : (
                          <VisibilityRoundedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: "primary.main",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(39,165,153,0.9)" },
                }}
              >
                {loading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "Sign In"
                )}
              </Button>
            </Box>

            {/* ===== LINK TO PATIENT ===== */}
            <Typography
              sx={{
                textAlign: "center",
                fontSize: 14,
                color: "text.secondary",
                mt: 3,
              }}
            >
              Are you a patient?{" "}
              <Box
                component={Link}
                to="/signin"
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  textDecoration: "none",
                }}
              >
                Patient Login
              </Box>
            </Typography>
          </Box>
        </motion.div>
      </Box>

      {/* ===== ERROR SNACKBAR ===== */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
      >
        <MuiAlert severity="error" variant="filled">
          {error}
        </MuiAlert>
      </Snackbar>

      {/* ===== WELCOME MODAL ===== */}
      <Dialog open={welcomeOpen}>
        <DialogTitle>Welcome</DialogTitle>
        <DialogContent>
          <Typography align="center">{welcomeMessage}</Typography>
        </DialogContent>
      </Dialog>
    </>
  );
}
